import { ApprovalRounded, CompareRounded } from '@mui/icons-material'
import { IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import ApiClient from '../../backend/ApiClient'
import { IApprovalRequest } from '../../definitions/Approval'
import Formatter from '../../helpers/Formatter'
import Helper from '../../helpers/Helper'
import ApprovalManager from '../../managers/ApprovalManager'
import { useAppDispatch } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import SmartCard from '../controls/cards/SmartCard'
import KPICard from '../controls/data_visualization/KPICard'
import { Column, SmartTable } from '../controls/table'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'

interface ApprovalTableProps {
    onManageApproval: (item: IApprovalRequest) => void
    onRowClick: (approval: IApprovalRequest) => void
}

const ApprovalTable = ({ onManageApproval, onRowClick }: ApprovalTableProps) => {
    const theme = useTheme()
    const dispatch = useAppDispatch()

    const { status, response } = useQuery({
        queryKey: buildQueryKey('get-user-approvals'),
        queryFn: () => ApiClient.getUserApprovals(),
        cache: false
    })

    const isLoading = status === 'loading'
    const approvals = response ?? []

    const liveItems = approvals.filter(item => item.status === 'approved')
    const draftItems = approvals.filter(item => item.status === 'draft')
    const readyForReviewItems = approvals.filter(item => item.status === 'ready-for-review')
    const rejectedItems = approvals.filter(item => item.status === 'rejected')

    const columns: Column<IApprovalRequest>[] = [
        { id: 'uuid', label: 'UUID', isPK: true, hidden: true },
        {
            id: 'entityName',
            label: 'Name',
            customRenderer: (cell, row, item) => (
                <span
                    style={{
                        cursor: 'pointer'
                    }}
                    onClick={e => {
                        e.stopPropagation()
                        onRowClick(item)
                    }}>
                    {cell.value}
                </span>
            )
        },
        {
            id: 'status',
            label: 'Status',
            customRenderer: cell => ApprovalManager.getStatusChip(cell.value as string)
        },
        { id: 'entityType', label: 'Type' },
        {
            id: 'custom',
            label: 'Last update',
            customRenderer: (cell, row, item) => <Typography variant='body2'>{Formatter.truncateIsoDate(item.changes[item.changes.length - 1].changedAt)}</Typography>
        },
        {
            id: 'action',
            label: 'Approval',
            customRenderer: (cell, row, item) => (
                <Stack flexDirection='row' gap={2}>
                    <Tooltip arrow title='Manage the approval'>
                        <IconButton
                            size='small'
                            onClick={e => {
                                e.stopPropagation()
                                onManageApproval(item)
                            }}>
                            <ApprovalRounded fontSize='small' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip arrow title={!item?.originalEntityUUID ? 'Live is not available to be compared' : 'Compare entity with its live.'}>
                        <span>
                            <IconButton
                                size='small'
                                disabled={!item?.originalEntityUUID}
                                onClick={e => {
                                    e.stopPropagation()
                                    dispatch(
                                        changeRoute({
                                            route: 'approval-audit',
                                            params: {
                                                entityName: item.entityName,
                                                entityType: item.entityType,
                                                approvalEntityUUID: item.approvalEntityUUID,
                                                originalEntityUUID: item.originalEntityUUID,
                                                approvalType: 'user'
                                            }
                                        })
                                    )
                                }}>
                                <CompareRounded fontSize='small' />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            )
        }
    ]

    return (
        <>
            <Tile.Row flex={0} spacing={2}>
                <SmartCard flex={1} style={{ borderColor: theme.palette.success.main }}>
                    <KPICard
                        data={Helper.dataframeToKPICard({ points: [{ x: 'current', y: liveItems.length }], delta: 0, deltaPercentage: 0 })}
                        label='APPROVED'
                        isLoading={isLoading}
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        data={Helper.dataframeToKPICard({ points: [{ x: 'current', y: draftItems.length }], delta: 0, deltaPercentage: 0 })}
                        label='DRAFT'
                        isLoading={isLoading}
                    />
                </SmartCard>
                <SmartCard flex={1} style={{ borderColor: theme.palette.primary.main }}>
                    <KPICard
                        data={Helper.dataframeToKPICard({ points: [{ x: 'current', y: readyForReviewItems.length }], delta: 0, deltaPercentage: 0 })}
                        inverse
                        label='READY FOR REVIEW'
                        isLoading={isLoading}
                    />
                </SmartCard>
                <SmartCard flex={1} style={{ borderColor: theme.palette.error.main }}>
                    <KPICard
                        data={Helper.dataframeToKPICard({ points: [{ x: 'current', y: rejectedItems.length }], delta: 0, deltaPercentage: 0 })}
                        inverse
                        label='REJECTED'
                        isLoading={isLoading}
                    />
                </SmartCard>
            </Tile.Row>
            <SmartCard table={true}>
                <SmartTable<IApprovalRequest>
                    hidePagination
                    key='approvals_table'
                    columns={columns}
                    noCollapse
                    datasource={{ items: approvals }}
                    toolbar={{
                        columns: true,
                        search: ['entityName', 'entityType', 'status'],
                        filters: true,
                        export: {
                            fileName: 'Approvals_table'
                        }
                    }}
                />
            </SmartCard>
        </>
    )
}

export default ApprovalTable
